import React, { useState, createContext, ReactNode } from "react";
import { Filter } from "utils/client/CRUD/types";
import { defaultUser } from "components/app-initialization/constants";
import { UserWithRole } from "components/app-initialization/types";

export type UserContextType = {
  user: UserWithRole;
  setUser: (user: UserWithRole) => void;
};

export type SpaceContextType = {
  space: string;
  setSpace: (space: string) => void;
};

export interface Field {
  id: string;
  label: string;
  hide?: boolean;
}

export interface Sort {
  field: string;
  order: "asc" | "desc";
}

export interface TablePreferenceType {
  id: string;
  fields: Field[];
  sort?: Sort;
}

export interface UserTablePreference {
  [key: string]: TablePreferenceType;
}

export type GoogleApiReceivedType = {
  apiKeyReceived: boolean;
  setApiKeyReceived: (apiKeyReceived: boolean) => void;
};

export type TablePreferencesType = {
  tablePreferences: UserTablePreference;
  setTablePreferences: (tablePreferences: UserTablePreference) => void;
};

const tablePreferencesDefault: TablePreferencesType = {
  tablePreferences: {},
  setTablePreferences: () => null,
};

export type RetrievedTablePreferencesType = {
  retrievedTablePreferences: boolean;
  setRetrievedTablePreferences: (retrievedTablePreferences: boolean) => void;
};

const retrievedTablePreferencesDefault: RetrievedTablePreferencesType = {
  retrievedTablePreferences: false,
  setRetrievedTablePreferences: () => null,
};

const userContextDefault: UserContextType = {
  user: defaultUser,
  setUser: () => null,
};

export type TableFiltersType = {
  tableFilters: { [key: string]: Filter[] };
  setTableFilters: (tableFilters: { [key: string]: Filter[] }) => void;
};

export const UserContext = createContext<UserContextType>(userContextDefault);
export const SpaceContext = createContext<SpaceContextType | null>(null);
export const TablePreferencesContext = createContext<TablePreferencesType>(tablePreferencesDefault);
export const RetrievedTablePreferencesContext = createContext<RetrievedTablePreferencesType>(retrievedTablePreferencesDefault);
export const GoogleApiReceivedContext = createContext<GoogleApiReceivedType | null>(null);
export const TableFiltersContext = createContext<TableFiltersType | null>(null);

type Props = {
  children: ReactNode;
};

export default function Store({ children }: Props) {
  const [user, setUser] = useState<UserWithRole>(defaultUser);
  const [space, setSpace] = useState("");
  const [tablePreferences, setTablePreferences] = useState<UserTablePreference>({});
  const [retrievedTablePreferences, setRetrievedTablePreferences] = useState(false);
  const [apiKeyReceived, setApiKeyReceived] = useState<any>(false);

  const [tableFilters, setTableFilters] = useState<{ [key: string]: Filter[] }>({});

  return (
    <UserContext.Provider value={{ user, setUser }}>
      <SpaceContext.Provider value={{ space, setSpace }}>
        <TablePreferencesContext.Provider value={{ tablePreferences, setTablePreferences }}>
          <RetrievedTablePreferencesContext.Provider value={{ retrievedTablePreferences, setRetrievedTablePreferences }}>
            <TableFiltersContext.Provider value={{ tableFilters, setTableFilters }}>
              <GoogleApiReceivedContext.Provider value={{ apiKeyReceived, setApiKeyReceived }}>{children}</GoogleApiReceivedContext.Provider>
            </TableFiltersContext.Provider>
          </RetrievedTablePreferencesContext.Provider>
        </TablePreferencesContext.Provider>
      </SpaceContext.Provider>
    </UserContext.Provider>
  );
}

export const UserContextConsumer = UserContext.Consumer;

export const RetrievedTablePreferencesConsumer = RetrievedTablePreferencesContext.Consumer;
